/**
 * destinationViewCache — how far the viewer had taken a destination page's
 * Sights section, so Back lands them where they were (Kurtis, 2026-10-23).
 *
 * Someone browsing sights opens card #30, reads the place page, and comes
 * Back. The page remounts (App.js KeyedDestination), and without this the list
 * fell back to 12 cards and the page to the top — card #30 was gone. The
 * overview and sections already come back from detailCache; this holds the
 * rest of the view: the "For you" / "Top picks" choice, how many cards were
 * shown, the whole pool "View more" fetched, and the window and strip scroll.
 *
 * Keyed by HISTORY ENTRY (React Router's location.key), so Back and Forward
 * find the view they left while a fresh visit to the same city — a new entry,
 * a new key — starts with 12 cards, as before. Keying by city alone let a
 * fresh visit overwrite the view an older entry would come Back to.
 *
 * Same module-level pattern as detailCache / feedCache. Also keyed by viewer so
 * an account switch never inherits another viewer's personal order. At most
 * MAX_VIEWS entries are kept (oldest dropped), since every visit is one. The TTL is
 * longer than the overview's 60 s on purpose: reading a place page takes
 * minutes, and a half-hour-old pool is still the night's ranking.
 *
 * Entry shape: { mode, count, pool: { sights, sights_personal, fetchedAt },
 * scrollY, stripScrollLeft, ts }. `ts` moves on every save; the pool carries
 * its own fetch time so going Back and Forth cannot keep an old pool alive.
 */

const viewCache = new Map();
const VIEW_TTL_MS = 30 * 60_000;
const MAX_VIEWS = 30;

export const buildDestinationViewKey = (kind, key, userId, entryKey) =>
  `${kind}:${key}:${userId || 'anon'}:${entryKey || 'default'}`;

// The entry, or null when there is none or it has expired.
export const getDestinationView = (cacheKey) => {
  if (!cacheKey) return null;
  const entry = viewCache.get(cacheKey);
  if (!entry) return null;
  if (Date.now() - entry.ts > VIEW_TTL_MS) {
    viewCache.delete(cacheKey);
    return null;
  }
  return entry;
};

// The view's "View more" pool when it is still within the TTL of its fetch,
// else null (the page then starts that entry at the first step again).
export const freshPool = (view) => (
  view?.pool && Date.now() - (view.pool.fetchedAt || 0) <= VIEW_TTL_MS ? view.pool : null
);

// Merges `patch` into the entry, so the page can save its list state as it
// changes and its scroll positions as it unmounts. `onlyIfPresent` updates an
// existing entry and never creates one: the unmount save runs after a
// sign-out or language change has cleared the cache, and must not leave a
// scroll-only entry behind for a list it no longer describes.
export const setDestinationView = (cacheKey, patch, { onlyIfPresent = false } = {}) => {
  if (!cacheKey) return;
  const prev = viewCache.get(cacheKey);
  if (onlyIfPresent && !prev) return;
  // Re-inserted, so Map order is least recently saved first.
  viewCache.delete(cacheKey);
  viewCache.set(cacheKey, { ...(prev || {}), ...patch, ts: Date.now() });
  while (viewCache.size > MAX_VIEWS) viewCache.delete(viewCache.keys().next().value);
};

export const clearDestinationViews = () => viewCache.clear();

export { VIEW_TTL_MS, MAX_VIEWS };
